/**
 * Recurring donations are a schedule that creates normal donor-added rows
 * as UK calendar dates arrive. Future occurrences are not donations.
 */

export const RECURRING_FREQUENCIES = [
  'weekly',
  'monthly',
  'quarterly',
  'annually',
] as const

export type RecurringFrequency = (typeof RECURRING_FREQUENCIES)[number]

export const RECURRING_END_CONDITION_TYPES = [
  'until_stopped',
  'on_date',
  'after_occurrences',
] as const

export type RecurringEndConditionType =
  (typeof RECURRING_END_CONDITION_TYPES)[number]

export type RecurringEndCondition =
  | { type: 'until_stopped' }
  | { type: 'on_date'; endDate: string }
  | { type: 'after_occurrences'; occurrenceCount: number }

export const RECURRING_STATUSES = ['active', 'stopped', 'completed'] as const

export type RecurringStatus = (typeof RECURRING_STATUSES)[number]

export const RECURRING_APPLY_SCOPES = ['future', 'all_eligible'] as const

export type RecurringApplyScope = (typeof RECURRING_APPLY_SCOPES)[number]

export const RECURRING_LIFECYCLE_EVENT_TYPES = [
  'started',
  'stopped',
  'restarted',
  'completed',
] as const

export type RecurringLifecycleEventType =
  (typeof RECURRING_LIFECYCLE_EVENT_TYPES)[number]

export type RecurringLifecycleEvent = {
  type: RecurringLifecycleEventType
  at: string
}

export type RecurringOccurrenceExceptionKind = 'skipped' | 'deleted'

/** A scheduled slot the engine must never create a donation for. */
export type RecurringOccurrenceException = {
  occurrenceDate: string
  kind: RecurringOccurrenceExceptionKind
}

export type RecurringDonationSeries = {
  id: string
  charity: string
  amountPence: number
  giftAidDeclared: boolean
  notes?: string
  frequency: RecurringFrequency
  /** Original first occurrence; never rewritten by later schedule edits. */
  firstDonationDate: string
  /**
   * Anchor used to compute the current schedule (first date, or the next date
   * chosen after a frequency change or restart). Always add periods from this
   * date so month-end days do not drift.
   */
  scheduleAnchorDate: string
  nextDonationDate: string | null
  endCondition: RecurringEndCondition
  status: RecurringStatus
  startedAt: string
  stoppedAt?: string
  completedAt?: string
  lifecycle: RecurringLifecycleEvent[]
  exceptions: RecurringOccurrenceException[]
}

export type RecurringDonationSeriesInput = {
  charity: string
  amountPence: number
  giftAidDeclared: boolean
  notes?: string
  frequency: RecurringFrequency
  firstDonationDate: string
  endCondition: RecurringEndCondition
  /** Required when the backfill would create an unusually large history. */
  confirmLargeBackfill?: boolean
}

export type RecurringDonationSeriesUpdate = {
  amountPence?: number
  giftAidDeclared?: boolean
  notes?: string
  frequency?: RecurringFrequency
  nextDonationDate?: string
  endCondition?: RecurringEndCondition
  applyScope?: RecurringApplyScope
}

export type RecurringDonationRestartInput = {
  amountPence: number
  giftAidDeclared: boolean
  notes?: string
  frequency: RecurringFrequency
  nextDonationDate: string
  endCondition: RecurringEndCondition
}

export type RecurringBackfillPreview = {
  dueCount: number
  taxYears: string[]
  /** First due date that would be added immediately, if any. */
  fromDate: string | null
  /** Last due date that would be added immediately, if any. */
  toDate: string | null
  requiresConfirmation: boolean
  exceedsLimit: boolean
  limit: number
  confirmThreshold: number
}
